/*
 * FILE: unified_ui/src/lib/report.ts
 * PURPOSE: Unified fire-and-forget event reporting from the SPA → POST /api/report
 *          → evt.observe.event → observe dispatcher sinks.
 * OWNS: reportEvent() helper for arbitrary app events (no schema — data passes through).
 * EXPORTS: reportEvent.
 *
 * Mirrors the serialized never-rejecting pipeline of trackEvent.ts: events are
 * POSTed one after another so the per-user chronological log stays ordered, and
 * a failed POST never blocks later events.
 */

/**
 * Serialized fire-and-forget pipeline. Events are POSTed one after another so
 * the per-user chronological log preserves order. The chain never rejects: a
 * failed POST must not block later events.
 */
import { authHeaders } from './sessionToken'

let pipeline: Promise<void> = Promise.resolve();

function enqueue(payload: Record<string, unknown>) {
  pipeline = pipeline
    .then(() =>
      fetch("/api/report", {
        method: "POST",
        headers: authHeaders({ "Content-Type": "application/json" }),
        credentials: "include",
        body: JSON.stringify(payload),
      }),
    )
    .then(() => undefined)
    .catch(() => {
      // fire-and-forget — never throw on reporting failure
    });
}

/**
 * Record an arbitrary app event. `userId` is optional — webBridge fills it from
 * the session when omitted. `data` is passed through verbatim (any shape).
 * `accountUserId` / `email` are optional extras forwarded into the POST body
 * only when provided (webBridge also fills them from the session). `username` /
 * `name` are optional messenger-visible identity forwarded only when provided.
 */
export function reportEvent(evt: {
  event: string;
  userId?: string;
  text?: string;
  data?: unknown;
  accountUserId?: string;
  email?: string;
  username?: string;
  name?: string;
}) {
  enqueue({
    event: evt.event,
    userId: evt.userId,
    text: evt.text,
    data: evt.data,
    ...(evt.accountUserId !== undefined ? { accountUserId: evt.accountUserId } : {}),
    ...(evt.email !== undefined ? { email: evt.email } : {}),
    ...(evt.username !== undefined ? { username: evt.username } : {}),
    ...(evt.name !== undefined ? { name: evt.name } : {}),
  });
}
